import { IMAGES } from "@/config/site";

const ITEMS = [
  {
    image: IMAGES.gallery1,
    caption: "Mentorship Seminar in Henderson",
    note: "Captures interactive education and classroom engagement.",
    testid: "gallery-item-1",
  },
  {
    image: IMAGES.gallery2,
    caption: "Barter & Trade Exchange Council",
    note: "Shows community exchange and mutual trade operations.",
    testid: "gallery-item-2",
  },
  {
    image: IMAGES.gallery3,
    caption: "Biomedical & Wellness Lab",
    note: "Highlights athletic conditioning and preventive wellness.",
    testid: "gallery-item-3",
  },
  {
    image: IMAGES.gallery4,
    caption: "Decentralized Server Repository",
    note: "Displays private merchant exchange server infrastructure.",
    testid: "gallery-item-4",
  },
];

const LEAGUE_ITEMS = [
  {
    src: "/assets/gallery/bgnzn-league-trio-maroon.jpeg",
    alt: "BGNZN league trio — maroon kit, Backyard Bump League",
    caption: "Home Kit — Maroon",
    wide: true,
    testid: "gallery-league-item-1",
  },
  {
    src: "/assets/gallery/bgnzn-league-trio-grey.jpeg",
    alt: "BGNZN league trio — slate kit, Backyard Bump League",
    caption: "Away Kit — Slate",
    wide: true,
    testid: "gallery-league-item-2",
  },
  {
    src: "/assets/gallery/bgnzn-league-portrait-1.webp",
    alt: "BGNZN league player portrait — front",
    caption: "Player Series · I",
    wide: false,
    testid: "gallery-league-item-3",
  },
  {
    src: "/assets/gallery/bgnzn-league-portrait-2.webp",
    alt: "BGNZN league player portrait — profile",
    caption: "Player Series · II",
    wide: false,
    testid: "gallery-league-item-4",
  },
  {
    src: "/assets/gallery/bgnzn-league-portrait-3.webp",
    alt: "BGNZN league player portrait — profile",
    caption: "Player Series · III",
    wide: false,
    testid: "gallery-league-item-5",
  },
];

const MERCH_ITEMS = [
  {
    src: "/assets/gallery/bgnzn-merch-hena-fig.jpeg",
    alt: "HENA FRUITS fig tee — Ficus carica botanical print",
    caption: "HENA FRUITS — Fig Tee",
    testid: "gallery-merch-item-1",
  },
  {
    src: "/assets/gallery/bgnzn-merch-crest-longsleeve.jpeg",
    alt: "BGNZN crest long-sleeve",
    caption: "Crest Long-Sleeve",
    testid: "gallery-merch-item-2",
  },
  {
    src: "/assets/gallery/bgnzn-merch-chief-3.jpeg",
    alt: "BGNZN sleeveless — chief portrait, number 3",
    caption: "Chief №3 Sleeveless",
    testid: "gallery-merch-item-3",
  },
  {
    src: "/assets/gallery/bgnzn-merch-polo-stripe.jpeg",
    alt: "BGNZN striped long-sleeve polo",
    caption: "Striped House Polo",
    testid: "gallery-merch-item-4",
  },
];

const Gallery = () => {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 px-6 border-b border-stone-800 bg-stone-950"
    >
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-3 max-w-xl">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Visual Archive &amp; Photos
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-100 display-serif">
              Institute Photo Gallery
            </h2>
            <p className="text-stone-400 text-sm">
              A dedicated visual record of mentorship sessions, trade activities, and council
              operations in Nevada.
            </p>
          </div>
          <div className="text-xs mono-tag text-stone-400 bg-stone-900 border border-stone-800 px-3 py-2 rounded-lg">
            Henderson · Las Vegas · Abroad
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {ITEMS.map((item) => (
            <div
              key={item.caption}
              data-testid={item.testid}
              className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3"
            >
              <div className="aspect-[4/3] rounded-lg border border-stone-800 relative overflow-hidden group">
                <img
                  src={item.image.url}
                  alt={item.image.alt}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-transparent pointer-events-none"></div>
                <p className="absolute bottom-0 left-0 right-0 p-4 text-[11px] text-stone-200 font-medium">
                  {item.caption}
                </p>
              </div>
              <div className="text-[11px] text-stone-400">{item.note}</div>
            </div>
          ))}
        </div>

        {/* ---------- BGNZN League — sports as a vocation (user-supplied photos) ---------- */}
        <div className="space-y-6 pt-6 border-t border-stone-800/60">
          <div className="space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              BGNZN League · Backyard Bump
            </span>
            <p className="text-stone-400 text-sm max-w-xl">
              The sports arm of the Sports &amp; Trades Academy — athletes train as professionals
              and learn the vocation of the game.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-6">
            {LEAGUE_ITEMS.map((item) => (
              <div
                key={item.caption}
                data-testid={item.testid}
                className={`rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3 ${
                  item.wide ? "col-span-2 lg:col-span-3" : "col-span-1 lg:col-span-2"
                }`}
              >
                <div className={`${item.wide ? "aspect-[4/3]" : "aspect-[3/4]"} rounded-lg border border-stone-800 relative overflow-hidden group`}>
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-transparent pointer-events-none"></div>
                  <p className="absolute bottom-0 left-0 right-0 p-4 text-[11px] text-stone-200 font-medium">
                    {item.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* ---------- BGNZN Apparel (user-supplied product photos) ---------- */}
        <div className="space-y-6 pt-6 border-t border-stone-800/60">
          <div className="space-y-2">
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              BGNZN Apparel · The House Line
            </span>
            <p className="text-stone-400 text-sm max-w-xl">
              Worn by the Institute — from the fruit-forest HENA FRUITS print to the league chief.
            </p>
            <a
              href="/shop"
              data-testid="gallery-shop-cta"
              className="inline-block mono-tag text-xs uppercase tracking-widest bg-amber-500 text-stone-950 font-bold px-5 py-2.5 rounded-lg hover:bg-amber-400 transition-colors"
            >
              Shop the line — pay in sUSD
            </a>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {MERCH_ITEMS.map((item) => (
              <div
                key={item.caption}
                data-testid={item.testid}
                className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3"
              >
                <div className="aspect-square rounded-lg border border-stone-800 relative overflow-hidden group bg-white">
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-transparent pointer-events-none"></div>
                  <p className="absolute bottom-0 left-0 right-0 p-4 text-[11px] text-stone-200 font-medium">
                    {item.caption}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
